/**
 * How ONE analysis-PDF section divides into pages.
 *
 * Two places need this answer and must never differ about it: PDFSection
 * renders the cards and prints the breaks between them, and the section packer
 * in bloodwork-analysis-pdf.tsx budgets the pages those cards land on. Every
 * pagination bug in this document so far has been the two sides answering the
 * same question apart — most recently an orphaned "Iron Status & Utilization"
 * title, because one side measured a page at 900px and the other at 1030.
 *
 * A section is at most three things stacked: an Insights panel, the results
 * card, and (when the card is longer than a page) more cards under "(cont.)"
 * headings. This module says where those cuts fall.
 *
 * Pure arithmetic — no DOM, no React — so both sides can ask it and a test can
 * ask it without a browser.
 */
import {
  BAR_GRAPH_BASE_HEIGHT,
  BAR_GRAPH_ROW_HEIGHT,
  BAR_GRAPH_TEXT_BLOCK_HEIGHT,
  barGraphsShareInsightsPage,
  textRowHeight,
} from './pdfPanelMetrics';
import {
  SectionRowChunk,
  SectionRowMetrics,
  packSectionRows,
  sectionChunkHeight,
} from '../utils/pdfSectionRowPacking';

/**
 * The packer's page budget, deliberately short of the 1073px a page really
 * holds: a section estimated a little long costs white space at the foot of a
 * page, and one estimated short costs a sliced card.
 *
 * Exported because the container packs pages with it and this module cuts
 * cards to it; they were separate copies of 900.
 */
export const PDF_SECTION_PAGE_CAPACITY = 900;

/** The gap between two stacked sections, and between a panel and its card. */
export const PDF_SECTION_GAP = 24;

/**
 * The most bar graphs on one card.
 *
 * Nutrient Status and Hormone Balance have split their charts at this
 * count for years (MAX_MARKERS_PER_BAR_PAGE in the container, which slices
 * those two lists before they get here). Every section reads it now, because
 * the ones a practitioner names have no ceiling on how many markers they hold
 * and nothing was cutting them: twenty charts printed one card, 1,220px of it,
 * against a page that holds 1,073.
 *
 * 17 charts measure 1,052 — past the budget below and inside a real page, which
 * is exactly what a card that owns its page should be.
 */
export const MAX_MARKERS_PER_CARD = 17;

export const SECTION_ROW_METRICS: SectionRowMetrics = {
  capacity: PDF_SECTION_PAGE_CAPACITY,
  baseHeight: BAR_GRAPH_BASE_HEIGHT,
  markerRowHeight: BAR_GRAPH_ROW_HEIGHT,
  textBlockHeight: BAR_GRAPH_TEXT_BLOCK_HEIGHT,
  maxMarkersPerChunk: MAX_MARKERS_PER_CARD,
};

/** One card of a section: which bar graphs and which text rows it carries. */
export type SectionCardChunk = SectionRowChunk & {
  /** 0 is the section's own heading; the rest print as "(cont.)". */
  index: number;
  isCont: boolean;
  /** What this card costs the page it lands on. */
  height: number;
};

/** The minimum a text marker has to look like to be measured. */
export type MeasurableTextMarker = {
  previousText?: string;
  isPreviousOnly?: () => boolean;
};

/**
 * The cards one section's results print as.
 *
 * Always at least one, so a section is never dropped for having nothing that
 * fits. The bar graphs all stay on the first card — see packSectionRows for
 * why that is a deliberate limit rather than an oversight.
 */
export function sectionCardChunks(
  markerCount: number,
  textMarkers: MeasurableTextMarker[],
): SectionCardChunk[] {
  // The rows are measured HERE, from the markers themselves, so the renderer
  // and the packer cannot measure the same row differently — a text result
  // that prints the previous collection's value underneath is a line taller,
  // and one side knowing that while the other does not is half a page.
  const textRowHeights = textMarkers.map(textRowHeight);
  return packSectionRows(markerCount, textRowHeights, SECTION_ROW_METRICS).map(
    (chunk, index) => ({
      ...chunk,
      index,
      isCont: index > 0,
      height: sectionChunkHeight(chunk, textRowHeights, SECTION_ROW_METRICS),
    }),
  );
}

/**
 * Does the results card open a page of its own, below the Insights panel?
 *
 * Asked of the FIRST card, which is the one that would sit under the panel. A
 * section long enough to be cut into cards fills that first one to the page, so
 * it always splits from its panel; a section that fits on one card asks exactly
 * the question PDFSection has always asked.
 */
export function cardStartsItsOwnPage(
  panelHeight: number,
  chunks: SectionCardChunk[],
): boolean {
  if (!(panelHeight > 0) || !chunks.length) {
    return false;
  }
  return !barGraphsShareInsightsPage(panelHeight, chunks[0].height);
}

/**
 * The pack units one section contributes, in the order they print.
 *
 * A section is ONE unit only in the simplest case. A panel that prints is its
 * own unit, because the card below it may or may not share its page and the
 * packer has to know which; and every "(cont.)" card is a unit, because each
 * one lands on a page of its own.
 *
 * THE LAST UNIT KEEPS THE SECTION'S OWN ID, so the JSX reads break-after for a
 * section exactly where it always has (`unitBreakAfter['LIVER']`), whatever the
 * section turned out to divide into.
 */
export type SectionPackUnit = {
  id: string;
  height: number;
  visible: boolean;
  /** True when the JSX/PDFSection prints a break above it, so the packer resets. */
  startsPage: boolean;
};

export function sectionPackUnits(input: {
  id: string;
  visible: boolean;
  panelHeight: number;
  markerCount: number;
  textMarkers: MeasurableTextMarker[];
  /** A section the JSX already opens with a break marker of its own. */
  forcedPageStart?: boolean;
}): SectionPackUnit[] {
  const {
    id,
    visible,
    panelHeight,
    markerCount,
    textMarkers,
    forcedPageStart,
  } = input;
  const chunks = sectionCardChunks(markerCount, textMarkers);
  const panelPrints = panelHeight > 0;
  const cardOwnPage = cardStartsItsOwnPage(panelHeight, chunks);

  const units: SectionPackUnit[] = [];
  /*
   * A panel the card SHARES a page with is not a unit of its own: the two are
   * one block on one page, and costing them apart is how the packer came to
   * break a pair the document prints together. The budget here is short of a
   * real page on purpose, so the pair often measures past it — which is the
   * right answer for what follows them (nothing else fits on that page) and the
   * wrong one for the pair itself.
   */
  const panelIsOwnPage = panelPrints && cardOwnPage;
  if (panelIsOwnPage) {
    units.push({
      id: `${id}_PANEL`,
      height: panelHeight,
      visible,
      startsPage: !!forcedPageStart,
    });
  }
  chunks.forEach((chunk, index) => {
    const isFirst = index === 0;
    units.push({
      // The LAST unit carries the section's own id, whatever the section
      // divided into, because that is where the JSX reads its break-after.
      id: index === chunks.length - 1 ? id : `${id}_CARD${index}`,
      height:
        isFirst && panelPrints && !cardOwnPage
          ? panelHeight + PDF_SECTION_GAP + chunk.height
          : chunk.height,
      visible,
      startsPage: isFirst
        ? // Either the panel above pushed this card onto its own page, or the
          // JSX opened the section with a break of its own.
          cardOwnPage || (!panelIsOwnPage && !!forcedPageStart)
        : // Every continuation card is reached by a break PDFSection prints.
          true,
    });
  });
  return units;
}
